import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Background refresh interval (5 minutes = 300,000 ms)
const BACKGROUND_REFRESH_INTERVAL = 5 * 60 * 1000;

// Time check interval (1 minute)
const TIME_CHECK_INTERVAL = 60 * 1000;

// Allowed time difference in milliseconds (5 minutes)
const ALLOWED_TIME_DIFF = 5 * 60 * 1000;

const AppSyncContext = createContext(null);

export const useAppSync = () => {
  const context = useContext(AppSyncContext);
  if (!context) {
    throw new Error('useAppSync must be used within AppSyncProvider');
  }
  return context;
};

export const AppSyncProvider = ({ children }) => {
  // Manual refresh trigger (user-initiated)
  const [manualRefreshTrigger, setManualRefreshTrigger] = useState(0);
  
  // Background refresh trigger (silent, every 5 minutes)
  const [backgroundRefreshTrigger, setBackgroundRefreshTrigger] = useState(0);
  
  const [lastManualRefresh, setLastManualRefresh] = useState(Date.now());
  const [lastBackgroundRefresh, setLastBackgroundRefresh] = useState(Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Time sync state - NO blocking, just warning
  const [isTimeSynced, setIsTimeSynced] = useState(true);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [timeDiff, setTimeDiff] = useState(0);
  const [serverTime, setServerTime] = useState(null);
  
  const backgroundIntervalRef = useRef(null);
  const timeCheckIntervalRef = useRef(null);

  // Check system time against server time (non-blocking)
  const checkSystemTime = useCallback(async () => {
    try {
      const clientTimeBeforeRequest = Date.now();
      const response = await axios.get(`${API}/server-time`);
      const clientTimeAfterRequest = Date.now();
      
      // Account for network latency
      const networkLatency = (clientTimeAfterRequest - clientTimeBeforeRequest) / 2;
      const serverTimestamp = new Date(response.data.server_time).getTime();
      const adjustedClientTime = clientTimeBeforeRequest + networkLatency;
      
      const difference = Math.abs(serverTimestamp - adjustedClientTime);
      setTimeDiff(difference);
      setServerTime(response.data.server_time);
      
      if (difference > ALLOWED_TIME_DIFF) {
        setIsTimeSynced(false);
        setShowTimeWarning(true);
        console.warn(`System time out of sync: ${Math.round(difference / 60000)} minutes difference`);
      } else {
        setIsTimeSynced(true);
        setShowTimeWarning(false);
      }
    } catch (error) {
      console.error('Error checking server time:', error);
      // Don't show warning on network error
      setIsTimeSynced(true);
    }
  }, []);

  // Initial time check on mount (non-blocking)
  useEffect(() => {
    checkSystemTime();
  }, [checkSystemTime]);

  // Periodic time check (every minute)
  useEffect(() => {
    timeCheckIntervalRef.current = setInterval(() => {
      checkSystemTime();
    }, TIME_CHECK_INTERVAL);

    return () => {
      if (timeCheckIntervalRef.current) {
        clearInterval(timeCheckIntervalRef.current);
      }
    };
  }, [checkSystemTime]);

  // Manual refresh function (user-initiated, with visual feedback)
  const triggerManualRefresh = useCallback(() => {
    setIsRefreshing(true);
    setManualRefreshTrigger(prev => prev + 1);
    setLastManualRefresh(Date.now());
    
    // Reset refreshing state after a short delay
    setTimeout(() => setIsRefreshing(false), 1000);
  }, []);

  // Background refresh every 5 minutes (silent, non-intrusive)
  useEffect(() => {
    backgroundIntervalRef.current = setInterval(() => {
      // Silent background refresh - no visual feedback
      setBackgroundRefreshTrigger(prev => prev + 1);
      setLastBackgroundRefresh(Date.now());
    }, BACKGROUND_REFRESH_INTERVAL);

    return () => {
      if (backgroundIntervalRef.current) {
        clearInterval(backgroundIntervalRef.current);
      }
    };
  }, []);

  // Dismiss time warning
  const dismissTimeWarning = useCallback(() => {
    setShowTimeWarning(false);
  }, []);

  // Retry time check manually
  const retryTimeCheck = useCallback(() => {
    checkSystemTime();
  }, [checkSystemTime]);

  // Combined refresh trigger (for pages that want to respond to both manual and background)
  const refreshTrigger = manualRefreshTrigger + backgroundRefreshTrigger;

  const value = {
    // Manual refresh (with UI feedback)
    manualRefreshTrigger,
    triggerManualRefresh,
    lastManualRefresh,
    isRefreshing,
    
    // Background refresh (silent)
    backgroundRefreshTrigger,
    lastBackgroundRefresh,
    
    // Combined trigger for convenience
    refreshTrigger,
    
    // Legacy support - triggerRefresh now does manual refresh
    triggerRefresh: triggerManualRefresh,
    lastRefresh: lastManualRefresh,
    
    // Time sync (non-blocking)
    isTimeSynced,
    showTimeWarning,
    timeDiff,
    serverTime,
    dismissTimeWarning,
    retryTimeCheck,
    
    // For backwards compatibility
    timeCheckPassed: true, // Always true - no blocking
    
    // Constants
    BACKGROUND_REFRESH_INTERVAL
  };

  return (
    <AppSyncContext.Provider value={value}>
      {children}
    </AppSyncContext.Provider>
  );
};

export default AppSyncProvider;
